import React, {useEffect, useMemo} from 'react';
import {SectionWrapper} from './TimeApprovalPage_styled';
import {APPROVAL_STATUS, BUTTON_COLOR, BUTTON_STYLE} from '../../../constants';
import TimeApprovalTable from './TimeApprovalTable';
import {Tabs, Tab} from 'web-components';
import {Button} from 'web-components';
import {getPersonData, getProjectData, GROUP_BY, ownedProjectPersonsQuery} from './TimeApprovalLogic';
import EmptyState from '../../../forecast-app/shared/components/empty-states/empty_state';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import Util from '../../../forecast-app/shared/util/util';
import SendTimeRegEmailMutation from '../../../mutations/send_time_reg_email_mutation';
import {useForecastFetchQuery} from '../../../forecast-app/shared/hooks/useForecastFetchQuery';
import {createToast} from '../../../forecast-app/shared/components/toasts/toast';
import CustomScrollDiv from "../../../forecast-app/shared/components/scroll-bars/custom_scroll_div";
const TimeApprovalSection = ({groupedTimeRegs, enabledColumns, currentViewingDate, intl, group, actualPersonId}) => {
	const {fetch, data} = useForecastFetchQuery(ownedProjectPersonsQuery);

	useEffect(() => {
		fetch({personId: actualPersonId});
	}, []);

	const sendMail = (personIds, onSuccess) => {
		Util.CommitMutation(
			SendTimeRegEmailMutation,
			{
				receiverIds: personIds,
				dateRange: currentViewingDate.clone().startOf('w').format('DD MMMM') + ' - ' + currentViewingDate.clone().endOf('w').format('DD MMMM YYYY'),
			},
			onSuccess
		);
	};
	const handleRemindAll = personIds => {
		const onSuccess = () => {
			createToast({duration: 5000, message: intl.formatMessage({id: 'approval.reminder_sent_to_all'})});
		};
		showModal({
			type: MODAL_TYPE.GENERIC,
			headerText: intl.formatMessage({id: 'timesheet.reminder_all_modal_header'}),
			content: <div className={'body-container'}>{intl.formatMessage({id: 'timesheet.reminder_all_modal_body'})}</div>,
			className: 'default-warning-modal',
			buttons: [
				{
					text: intl.formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: intl.formatMessage({id: 'common.send'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.GREEN,
					callback: () => sendMail(personIds, onSuccess),
				},
			],
		});
	};

	const handleRemindOne = person => {
		const onSuccess = () => {
			createToast({duration: 5000, message: intl.formatMessage({id: 'approval.reminder_sent_to_person'}, {person: person.firstName || person.lastName})});
		};
		showModal({
			type: MODAL_TYPE.GENERIC,
			headerText: intl.formatMessage({id: 'timesheet.reminder_modal_header'}),
			content: <div className={'body-container'}>{intl.formatMessage({id: 'timesheet.approval_reminder_modal_body'}, {person: person.firstName + ' ' + person.lastName})}</div>,
			className: 'default-warning-modal',
			buttons: [
				{
					text: intl.formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: intl.formatMessage({id: 'common.send'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.GREEN,
					callback: () => sendMail([person.id], onSuccess),
				},
			],
		});
	};

	/**
	 * We need to calculate the first groupings for all tab first to get the numbers and the empty states correctly
	 */
	const firstGroupData = useMemo(() => {
		let res = {};
		if (group === GROUP_BY.PROJECT) {
			res[APPROVAL_STATUS.NOT_SUBMITTED] = getProjectData(groupedTimeRegs[APPROVAL_STATUS.NOT_SUBMITTED]);
			res[APPROVAL_STATUS.SUBMITTED] = getProjectData(groupedTimeRegs[APPROVAL_STATUS.SUBMITTED]);
			res[APPROVAL_STATUS.APPROVED] = getProjectData(groupedTimeRegs[APPROVAL_STATUS.APPROVED]);
		} else {
			res[APPROVAL_STATUS.SUBMITTED] = getPersonData(groupedTimeRegs[APPROVAL_STATUS.SUBMITTED]);
			res[APPROVAL_STATUS.APPROVED] = getPersonData(groupedTimeRegs[APPROVAL_STATUS.APPROVED]);
			res[APPROVAL_STATUS.NOT_SUBMITTED] = getPersonData(groupedTimeRegs[APPROVAL_STATUS.NOT_SUBMITTED], null, data?.viewer.company.person.ownedProjectPersons.edges, res);
		}
		return res;
	}, [data, currentViewingDate, group, groupedTimeRegs]);

	const uniquePersonIdsWithUnsubmittedTimeRegs = group === GROUP_BY.PERSON ? firstGroupData[APPROVAL_STATUS.NOT_SUBMITTED].map(p => p.person.id) : getPersonData(groupedTimeRegs[APPROVAL_STATUS.NOT_SUBMITTED]).map(p => p.person.id);
	const submittedCount = firstGroupData[APPROVAL_STATUS.SUBMITTED].length;
	const approvedCount = firstGroupData[APPROVAL_STATUS.APPROVED].length;
	const notSubmittedCount = firstGroupData[APPROVAL_STATUS.NOT_SUBMITTED].length;

	const children = selectedTab => {
		if (firstGroupData[selectedTab].length === 0) {
			if (submittedCount + approvedCount + notSubmittedCount === 0) {
				return <EmptyState pageName={EmptyState.EMPTY_STATE.APPROVAL_NO_REGISTERED} />;
			} else if (submittedCount + approvedCount === 0) {
				return <EmptyState pageName={EmptyState.EMPTY_STATE.APPROVAL_NO_SUBMIT} />;
			} else if (submittedCount === 0 && approvedCount > 0 && notSubmittedCount > 0) {
				return <EmptyState pageName={EmptyState.EMPTY_STATE.APPROVAL_MISSING_SUBMITION} />;
			} else if (approvedCount === 0) {
				return <EmptyState pageName={EmptyState.EMPTY_STATE.APPROVAL_NOTHING_APPROVED} />;
			} else if (submittedCount + notSubmittedCount === 0) {
				return <EmptyState pageName={EmptyState.EMPTY_STATE.APPROVAL_ALL_APPROVED} />;
			} else if (submittedCount > 0 && notSubmittedCount === 0) {
				return <EmptyState pageName={EmptyState.EMPTY_STATE.APPROVAL_ALL_SUBMITTED} />;
			}
		} else {
			return (
				<>
					<div style={{position: 'sticky', left: '0', padding: '16px 16px', width: '100%', display: 'flex', justifyContent: 'flex-end'}}>
						{selectedTab === APPROVAL_STATUS.NOT_SUBMITTED && (
							<Button variant={Button.VARIANT.VERY_LIGHT_GRAY_OUTLINE} onClick={() => handleRemindAll(uniquePersonIdsWithUnsubmittedTimeRegs)}>
								{intl.formatMessage({id: 'timesheet.reminder_all_modal_header'})}
							</Button>
						)}
					</div>
					<TimeApprovalTable
						onRemind={handleRemindOne}
						sectionType={selectedTab}
						currentViewingDate={currentViewingDate}
						intl={intl}
						enabledColumns={enabledColumns}
						isGroupedByProject={group === GROUP_BY.PROJECT}
						firstGroupData={firstGroupData[selectedTab]}
					/>
				</>
			);
		}
	};

	return (
		<SectionWrapper>
			<Tabs>
				<Tab cy={'time-approval-tab-submitted'} defaultSelected title={intl.formatMessage({id: 'time_approval.submitted'}) + ' (' + firstGroupData[APPROVAL_STATUS.SUBMITTED].length + ')'}>
					<CustomScrollDiv>
						{children(APPROVAL_STATUS.SUBMITTED)}
					</CustomScrollDiv>
				</Tab>
				<Tab cy={'time-approval-tab-not-submitted'} title={intl.formatMessage({id: 'time_approval.not_submitted'}) + ' (' + firstGroupData[APPROVAL_STATUS.NOT_SUBMITTED].length + ')'}>
					<CustomScrollDiv>
						{children(APPROVAL_STATUS.NOT_SUBMITTED)}
					</CustomScrollDiv>
				</Tab>
				<Tab cy={'time-approval-tab-approved'} title={intl.formatMessage({id: 'common.approved'}) + ' (' + firstGroupData[APPROVAL_STATUS.APPROVED].length + ')'}>
					<CustomScrollDiv>
						{children(APPROVAL_STATUS.APPROVED)}
					</CustomScrollDiv>
				</Tab>
			</Tabs>
		</SectionWrapper>
	);
};

export default TimeApprovalSection;
